import * as Sentry from '@sentry/vue'
import { useProjectStore } from '@/stores/project'
import { useClientStore } from '@/stores/client'
import { useCoreSocket } from '@/services/socket/core-socket'
import type { UpdateDataType } from '@/components/partials/display/VersionUpdate.vue'
import {
  analyzeProjectImport,
  buildMergedProject,
  classifyVersionUpdates,
  prepareProjectSource,
  type CurrentProjectContext,
  type ParsedProjectImport,
  type ProjectImportAnalysis,
} from '@/helpers/project-transfer'

/**
 * Shared flow for loading a saved project state into the current project, used
 * by the share-file import and by "reset to this state" on the summary page.
 *
 * prepare: analyse role/country compatibility, validate the merged project on
 * the server (same schemas as host-injected data) and classify the reported
 * parameter updates. A major version difference blocks loading.
 * apply: replace the project and run the automatic patch/minor migrations.
 */

export type PreparedProjectLoadErrorCode =
  | 'no_project'
  | 'socket_unavailable'
  | 'validation_failed'
  | 'timeout'
  | 'busy'
  | 'incompatible_version'

export interface PreparedProjectLoadError {
  code: PreparedProjectLoadErrorCode
  message?: string
  parts?: string[]
}

export interface PreparedProjectLoad {
  ok: boolean
  analysis: ProjectImportAnalysis | null
  source: ParsedProjectImport
  validatedProject: Record<string, unknown> | null
  compatibleUpdates: UpdateDataType[]
  error: PreparedProjectLoadError | null
}

export interface ProjectStorageContext {
  userId: string
  refreshToken: string
}

/** userId + refresh token: the same requirements as the project mirror and stored PDFs. */
export function getProjectStorageContext(): ProjectStorageContext | null {
  const projectStore = useProjectStore()
  const clientStore = useClientStore()
  const userId = projectStore.getMeta?.userId
  const refreshToken = clientStore.refreshToken
  if (typeof userId === 'string' && userId.length > 0 && typeof refreshToken === 'string' && refreshToken.length > 0) {
    return { userId, refreshToken }
  }
  return null
}

export function getCurrentProjectContext(): CurrentProjectContext | null {
  const projectStore = useProjectStore()
  const project = projectStore.getProject
  if (!project) return null
  return {
    project,
    editableRoles: projectStore.getValidRoles,
    canEdit: !!projectStore.getMeta?.canEdit,
  }
}

export async function prepareProjectLoad(
  project: Record<string, unknown>,
  meta: Record<string, unknown> | null | undefined,
  savedAt: number | null = null
): Promise<PreparedProjectLoad> {
  const source = prepareProjectSource(project, meta, savedAt)
  const base: PreparedProjectLoad = {
    ok: false,
    analysis: null,
    source,
    validatedProject: null,
    compatibleUpdates: [],
    error: null,
  }

  const context = getCurrentProjectContext()
  if (!context) {
    return { ...base, error: { code: 'no_project' } }
  }

  const analysis = analyzeProjectImport(source, context)
  if (!analysis.ok) {
    return { ...base, analysis }
  }

  const messageCenter = useCoreSocket().messageCenter
  if (!messageCenter) {
    return { ...base, analysis, error: { code: 'socket_unavailable' } }
  }

  const merged = buildMergedProject(source, context, analysis)
  const meta_ = (useProjectStore().getMeta ?? {}) as Record<string, unknown>
  const result = await messageCenter.validateProjectImport(meta_, merged)

  if (!result.ok) {
    return {
      ...base,
      analysis,
      error: { code: result.reason, message: result.reason === 'validation_failed' ? result.message : undefined },
    }
  }

  const { compatible, incompatibleParts } = classifyVersionUpdates(result.project, result.updates)
  if (incompatibleParts.length) {
    return {
      ...base,
      analysis,
      validatedProject: result.project,
      error: { code: 'incompatible_version', parts: incompatibleParts },
    }
  }

  return {
    ...base,
    ok: true,
    analysis,
    validatedProject: result.project,
    compatibleUpdates: compatible,
  }
}

export async function applyPreparedProjectLoad(prepared: PreparedProjectLoad): Promise<boolean> {
  if (!prepared.ok || !prepared.validatedProject || !prepared.analysis) return false

  const projectStore = useProjectStore()
  await projectStore.applyImportedProject(prepared.validatedProject, { updates: prepared.compatibleUpdates })

  Sentry.logger.info('Project state loaded', {
    parts: prepared.analysis.importedParts,
    skippedRoles: prepared.analysis.skippedRoles,
    autoUpdates: prepared.compatibleUpdates.map((update) => `${update.part}@${update.version}`),
  })
  return true
}
